import { getLogger } from '@kit/shared/logger';

interface TranslationResource {
  [key: string]: string | TranslationResource;
}

/**
 * Deep merge two translation objects. Override values take precedence over base values.
 * Only plain objects are recursively merged — arrays and primitives are replaced entirely.
 *
 * Example:
 *   base:      { courts: "Courts", calendar: { chooseCourt: "Choose a court" } }
 *   overrides: { courts: "Rooms",  calendar: { chooseCourt: "Choose a room"  } }
 *   result:    { courts: "Rooms",  calendar: { chooseCourt: "Choose a room"  } }
 */
function deepMerge(
  base: TranslationResource,
  overrides: TranslationResource,
): TranslationResource {
  const result = { ...base };

  for (const key of Object.keys(overrides)) {
    const baseVal = result[key];
    const overrideVal = overrides[key]!;

    if (
      typeof baseVal === 'object' &&
      baseVal !== null &&
      typeof overrideVal === 'object' &&
      overrideVal !== null
    ) {
      result[key] = deepMerge(
        baseVal as TranslationResource,
        overrideVal as TranslationResource,
      );
    } else {
      result[key] = overrideVal;
    }
  }

  return result;
}

/**
 * Business types that do not need translation overrides.
 *
 * - 'sports' and 'padel_court' use the default sports terminology ("courts", "coaches").
 *   'padel_court' is the legacy value, kept for backward compatibility.
 *
 * Any business type NOT in this set (e.g., 'clinic', 'restaurant') will trigger an
 * attempt to load a `<namespace>.<businessType>.json` override file.
 */
const DEFAULT_BUSINESS_TYPES = new Set([
  'sports',
  'padel_court',
]);

/**
 * @name i18nResolver
 * @description Resolve the translation file for the given language and namespace.
 * When a businessType is provided and has override translations, they are
 * deep-merged on top of the base namespace translations. Override files are sparse —
 * they only need to contain keys that differ from the base; all other keys fall back
 * to the base file.
 * @param language - BCP 47 language tag (e.g., 'en', 'ar')
 * @param namespace - Translation namespace (e.g., 'court', 'bookings')
 * @param businessType - Optional business type for loading translation overrides (e.g., 'clinic')
 */
export async function i18nResolver(
  language: string,
  namespace: string,
  businessType?: string | null,
): Promise<TranslationResource> {
  const logger = await getLogger();

  let baseData: TranslationResource = {};

  try {
    // Dynamic import is resolved at build time by webpack/turbopack into
    // separate chunks per locale file. The path must use string literals
    // or template literals with a fixed prefix for the bundler to trace them.
    const module = (await import(
      `../../public/locales/${language}/${namespace}.json`
    )) as TranslationResource & { default?: TranslationResource };

    // JSON dynamic imports may expose the parsed object as `.default` when
    // using ESM interop (esModuleInterop=true + moduleResolution=bundler).
    // Fall back to the module itself if `.default` is absent.
    baseData = module.default ?? module;
  } catch (error) {
    logger.error(
      {
        language,
        namespace,
        error: error instanceof Error ? error.message : String(error),
      },
      `Missing translation file: public/locales/${language}/${namespace}.json`,
    );

    return {};
  }

  // Skip override lookup for business types that use the default terminology.
  if (!businessType || DEFAULT_BUSINESS_TYPES.has(businessType)) {
    return baseData;
  }

  // Attempt to load business-type-specific overrides (e.g., court.clinic.json).
  // The override file is sparse — it only needs to contain keys that differ from
  // the base. Missing keys in the override fall through to base translations.
  try {
    const overrideModule = (await import(
      `../../public/locales/${language}/${namespace}.${businessType}.json`
    )) as TranslationResource & { default?: TranslationResource };

    const overrideData = overrideModule.default ?? overrideModule;

    return deepMerge(baseData, overrideData);
  } catch {
    // Silent fallback: no override file exists for this namespace/businessType combination.
    // This is the expected path for most namespaces (e.g., 'auth.clinic.json' does not exist).
    return baseData;
  }
}
